import { T_LoanIntelligence } from "store/loanIntelligence";
import { T_MongoId, T_MongoDoc } from "../types/_utils.types";
import { Gateway } from "./wallet.transaction.dto";
import { OrganisationDto } from "./organisation.dto";
import { UserDto } from "./user.dto";

export enum AVAILABLE_CATEGORIES {
  LOAN = "loan",
  BANK = "bank",
  LAWSUIT = "lawsuit",
  AGREEMENT = "agreement",
  OTHER = "other",
}

export enum StrategySource {
  CUSTOMER_API = "customer", // GetClear
  ADMIN_API = "admin", // BlogAPI
}

export enum PaymentMethods {
  CREDIT_CARD = "cc",
  ECHECK = "ck",
  PAYNOTE = "paynote",
  PAYNOTE_PLAID = "paynote x plaid",
}

export enum PDFTypes {
  STRATEGY_ONE = "strategy_one",
  STRATEGY_TWO = "strategy_two",
  // STRATEGY_THREE = "strategy_three",
  // CUSTOM = "custom",
  CITADEL = "citadel",
  LYL = "lyl",
  ECR = "ecr",
}

export type ClientDetails = {
  ssn?: string;
  state?: string;
  city?: string;
  zip_code?: number;
  phone?: string;
  street_address?: string;
  status?: string;
  weekly_budget?: number;
  profit_margin?: number;
  name: string;
  alternate_phone?: number;
  cosigners?: {
    name: string;
    email: string;
  }[];
};

export type BusinessInfo = {
  company_name: string;
  ein_number?: string;
  business_type?: string;
  description?: string;
  state?: string;
  city?: string;
  zip_code?: number;
  phone?: string;
  email?: string;
  street_address?: string;
};

export type SelectedBank = {
  bank_name: string;
  account_no: string;
  name_on_account: string;
  routing_no: number;
};

export enum ManagerApprovalStatus {
  NONE = "none",
  REQUESTED = "requested",
  APPROVED = "approved",
  SUGGESTED = "suggested",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
}

export type MetricDocument = {
  revenue: {
    monthly: number;
    annual: number;
  };
  trueRevenue: {
    monthly: number;
    annual: number;
  };
  expenses: {
    monthly: number;
    annual: number;
  };
  profit: {
    monthly: number;
    annual: number;
  };
  balanceDaysNegative: {
    balance: number;
    daysNegative: number;
  };
  nonTrueRevenue: {
    monthly: number;
    annual: number;
  };
  lowDays: number | null;
  trueBalance: number | null;
  daysWithReturns: number | null;
  mcaWithholdPercent: string | null;
  combinedDaysNegative: number | null;
  averageMonthlyNetRevenue: {
    monthly: number;
    annual: number;
  };
  averageMonthlyFactoringRevenue: {
    monthly: number;
    annual: string;
  };
  minimumMonthlyTrueRevenue: number | null;
  averageMonthlyCreditCardRevenue: {
    monthly: number | null;
    annual: string;
  };
};

enum LOAN_LOCK_FLAGS {
  PENDING = "pending",
  PROCESSING = "processing",
  DONE = "done",
  NONE = "",
}
export enum StrategyType {
  STANDARD = "standard",
  PERCENTAGE = "percentage",
}

export enum TeamRole {
  OPENER = "opener",
  NEGOTIATOR = "negotiator",
  CSM = "customer_success_manager",
  ACSM = "associate_customer_success_manager",
  CLOSER = "closer",
  /** Can be multiple */
  LEGAL = "legal",
  UNDERWRITER = "underwriter",
  /** Can be multiple */
  PROTECTION = "protection",
}

export enum PlaidConnectionStatus {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
  PENDING = "pending",
  NONE = "none",
}

export type TeamMember = {
  user: T_MongoId;
  role: TeamRole;
};

export enum MeetingType {
  ONBOARDING = "onboarding",
  CARD_PROCESS = "card_process",
  OTHERS = "others",
}

export type Meeting = {
  title: string | undefined;
  type: MeetingType;
  link: string;
  time: string;
};

export type GetClearApplicationDto = {
  app_key: string;
  user: T_MongoId;
  loans: T_MongoId[];
  loan_lock?: LOAN_LOCK_FLAGS | "";
  banks: T_MongoId[];
  statement_files?: T_MongoId[];
  selected_bank?: SelectedBank;
  lawsuits: T_MongoId[];
  lawsuit_insurance?: boolean;
  agreements: T_MongoId[];
  dms_id?: string;
  dms_customer_vault_id?: string;
  external_links: {
    mt_app_number?: number;
    pandadoc?: {
      name: string;
      doc_id: string;
      status: string;
      regeneration_count: number;
      // strategy_used: string;
      weekly_payments: number;
      session_id?: string;
      expires_at?: Date;
      // sent_link?: string;
      recipients: {
        id?: string;
        email: string;
        first_name: string;
        last_name: string;
        role: "Client" | "Cosigner";
        sent_link?: string;
      }[];
    }[];
  };
  isPriorApplication: boolean;
  strategy_type?: StrategyType;
  client_details: ClientDetails;
  business_info: BusinessInfo;
  accounts_connected: {
    bank_name: string;
    bank_link: any;
    meta: any;
    account_number: string;
  }[];
  summary: {
    current_debt: number;
    weekly_payments: number;
    monthly_revenue?: number;
    monthly_profit?: number;
    money_thumb?: {
      metrics: MetricDocument;
      // additional fields
      accountslist?: [];
      thumbprints?: [];
      monthlymca?: [];
      mcacompanies?: [];
      reptrans?: [];
      mcatrans?: [];
      nsftrans?: [];
      overtrans?: [];
      dailybalance?: [];
      dailycashflow?: [];
      monthlycashflow?: [];
      credittrans?: [];
      truecredittrans?: [];
      untruecredittrans?: [];
      xferitrans?: [];
      xferotrans?: [];
      largetrans?: [];
      returnedtrans?: [];
      monthlynegativedays?: [];
      mobilepayments?: [];
      investments?: [];
      monthlyrecovery?: [];
      casinotrans?: [];
      // anything else
      meta?: any;
    };
    total_advance_amount: number;
    transaction_data?: any;
    plaid?: any;
  };

  dms_response?: any;
  status: {
    last_step: string;
    onboarding: boolean;
    loans: boolean;
    lawsuits: boolean;
    banks: boolean;
    agreements: boolean;
    accounts_connected: boolean;
    strategy: boolean;
    signatures: boolean;
    personal_info: boolean;
    advance_paid: boolean;
  };
  payment_status: {
    initial_paid: PAYMENT_STATUS;
    initial_method?: string;
    service_initiated: PAYMENT_STATUS;
    service_method?: string;
    program_initiated: PAYMENT_STATUS;
    program_method?: string;
  };
  strategy?: {
    custom_payments: boolean;
    plan: string;
    initial_payment: number;
    weekly_payments: number;
    interest?: number;
    interest_rate?: number;
    service_weekly?: number;
    tenure: number;
    program_tenure: number;
    lawsuit_initial_payment: number;
    lawsuit_monthly_payment: number;
    extras?: {
      is_custom_divided: boolean;
      is_managers_approval: boolean;
      selector_idx: number;
      strategy_clause: number;
      split_rows?: any;
      is_initial_split: boolean;
      initial_amount?: number;
      initial_split?: number;
      split_at: number;
    };
  };
  tables?: {
    enrolled_creditors?: any;
    program_summary?: any;
    payment_schedule?: any;
  };
  messages?: T_MongoId[];
  referrer_code?: string;
  temporary_attachments?: T_MongoId[];
  ai_responses?: any;
  graphs?: any;
  org_code?: ORGANISATIONS | string;
  payment_methods?: {
    name?: string;
    amount?: number;
    start_date?: Date;
    method_type: "seamless" | "easypay" | "actum" | "pg"; // pg for general payment gateway
  }[];
  payment_tokens?: {
    actum?: string;
    seamless?: string;
    easypay?: string;
  };
  managers_approval?: {
    status: MANAGERS_APPROVAL_STATUS;
    budget?: number;
    last_updated?: Date;
    selected?: string;
    selection_idx?: number;
  };
  application_type?: ApplicationType;
  plaid_config?: {
    plaid_user_id?: string;

    link_token?: string;
    link_token_expiry?: string;
    hosted_link_url?: string;
    hosted_link_expiry?: string;

    item_id?: string;
    access_token?: string;

    asset_report_id?: string;
    asset_report_token?: string;
    asset_report_complete?: boolean;

    last_txn_cursor?: string;
    auto_relink_sent?: boolean;

    status?: PlaidConnectionStatus;
  };
  bank_fetch_via?: BankFetchMethod;
  viewers?: T_MongoId[];
  editors?: T_MongoId[];
};

enum PAYMENT_STATUS {
  INITIATED = "initiated",
  NOT_INITIATED = "not-initiated",
  IN_PROCESS = "processing",
  FAILED = "failed",
  DECLINED = "declined",
  ERROR = "error",
  SUCCESS = "paid",
  NOT_REQUIRED = "not-required",
}

enum ORGANISATIONS {
  FCDS25 = "fcds25",
  // ELITED = "edp001",
  SSA001 = "ssa001",
  BFS001 = "bfs001",
  EFP001 = "efp001",
  ECR001 = "ecr001",
  DEFAULT = "default",
}

enum MANAGERS_APPROVAL_STATUS {
  NONE = "none",
  REQUESTED = "requested",
  APPROVED = "approved",
  SUGGESTED = "suggested",
  REJECTED = "rejected",
  CANCELLED = "cancelled",
}

export enum ApplicationType {
  GENERAL = "",
  DROP = "drop",
  AFFILIATE = "affiliate",
}

export enum BankFetchMethod {
  MONEYTHUMB = "MoneyThumb",
  PLAID = "Plaid",
}

// export enum Frequency {
//   DAILY = "Daily",
//   WEEKLY = "Weekly",
//   MONTHLY = "Monthly",
//   YEARLY = "Yearly",
// }

type _Status = keyof Omit<GetClearApplicationDto["status"], "last_step">;
export const APPLICATION_STATUS = {
  ONBOARDING: "onboarding",
  LOANS: "loans",
  BANKS: "banks",
  LAWSUITS: "lawsuits",
  AGREEMENTS: "agreements",
  ADVANCE_PAID: "advance_paid",
  ACCOUNTS_CONNECTED: "accounts_connected",
  STRATEGY: "strategy",
  PERSONAL_INFO: "personal_info",
  SIGNATURES: "signatures",
} satisfies { [K in Uppercase<_Status>]: Lowercase<K> };

export type GetClearApplicationSchemaDto = GetClearApplicationDto & T_MongoDoc;
export type CreateApplicationDto = ApplicationSchemaDto;
export type UpdateApplicationDto = Partial<ApplicationSchemaDto>;

type TweakParametersDto = {
  weekly_budget: number;
  weekly_sales: number;
  weekly_sales_percentage: number;
  sales_value: number;
  profit_margin: number;
  profit_percentage: number;
  agency_commission: number;
};

enum Frequency {
  WEEKLY = "weekly",
  MONTHLY = "monthly",
  DAILY = "daily",
  YEARLY = "yearly",
}

enum Priority {
  NONE = "None",
  NORMAL = "Normal",
  HIGH = "High",
}

export type PaymentEntryTable = {
  week: string;
  payment: number;
  program_fee: number;
  service_fee: number;
  legal_fee?: number;
  creditor: number;
  // createdAt: string;
  // updatedAt: string;
};
type CreditorDto = {
  loan_id?: any;
  creditor_name: string;
  funded_amount: number;
  payback_amount?: number;
  current_due: number;
  payment_frequency: Frequency;
  weekly_payment: number;
  payment_amount: number;
  tenure?: number;
  weightage: number;
  priority: Priority;
  aggression_level: number;
};

export type T_CustomStrategy = {
  weekly_budget: number;
  initial_payment: number;
  initial_payment_timeline: number;
  net_commission: number;
  commission_timeline: number;
  display_timeline?: number;
  creditor_estimate: number;
  weeks: number;
  include_commission_payment: boolean;
  original_weeks: number;
  lawsuit_initial: number;
  lawsuit_monthly: number;
  insurance_one_time: number;
  insurance_monthly: number;
  service_weekly: number;
  card_mdr: number;
  multiplier: number;
  total_interest: number;
  initial_pay: number;
  bias_amount: number;
  pushback_factor: number;
  creditor_estimate_split: { amount: number; weeks: number }[];
  initial_payment_charge: number;
  tables?: any;
  program_part: number;
  success_part: number;
};

export enum TABLE_STATUS {
  INITIAL = "initial",
  RECREATED = "recreated",
  UPDATED = "updated",
  CANCELLED = "cancelled",
}

export type Summary = {
  current_debt?: number;
  weekly_payments?: number;
  weekly_budget?: number;
  monthly_revenue?: number;
  /** Store this in amount */
  monthly_profit?: number;
  /** Store this in percentage, in case profit is missing then calculate using revenue and percentage  */
  profit_percentage?: number;
  total_advance_amount?: number;
  lawsuit_insurance?: boolean;
};

export enum AutopayStatus {
  NONE = "none",
  ACTIVE = "active",
  TERMINAL_FAILURE = "terminal_failure",
  CANCELLED = "cancelled",
  COMPLETE = "complete",
}

export type ApplicationDto = {
  _id: T_MongoId;
  lead: T_MongoId;
  app_key: string;
  affiliate?: UserDto;
  summary?: Summary;
  meta: {
    client_name?: string;
    business_name?: string;
    email?: string;
    phone?: string;
    address?: string;
  };
  payment_table?: {
    status: TABLE_STATUS;
    created: string;
    started?: string;
    version?: number;
    headers: ["week", "payment", "program_fee", "service_fee", "legal_fee", "creditor"];
    first_row: PaymentEntryTable;
    repeat_rows: { row: PaymentEntryTable; count: number }[];
    totals: PaymentEntryTable;
    weeks: number;
    lawsuit?: boolean;
    update_purpose_code?: string;
  }[];
  actum_ach?: {
    last_try?: string;
    condition: boolean;
    status: string;
    merordernumber?: string;
    responses: any[];
  };
  team_members?: TeamMember[];
  meetings?: Meeting[];
  loans?: CreditorDto[]; // add creditors here as loans: []
  tweak_parameter?: TweakParametersDto;
  total_loans?: number;
  total_banks?: number;
  total_lawsuits?: number;
  enrolled_debt?: number;
  last_step?: string;
  onboarding_completed?: boolean;
  payments?: T_MongoId[];
  strategy_type: StrategyType;
  strategy_source: StrategySource;
  plan?: {
    slug: string;
    key: string;
    title: string;
    initial_payment: number;
    weekly_payments: number;
    tenure: number;
    lawsuit_initial_payment: number;
    lawsuit_monthly_payment: number;
    custom_payments: boolean;
  };
  isPriorApplication: boolean;
  intelligence?: {
    category?: AI_INTEL;
    primary_report?: T_MongoId;
    chat_history: {
      role: AI_ROLES;
      type: AI_MSG_TYPE;
      report?: T_MongoId;
      message?: string;
      timestamp: string;
    }[];
  }[];
  organisation?: OrganisationDto;
  archived?: boolean;
  source_uri?: string;
  createdAt?: string;
  loan_intelligence?: T_LoanIntelligence;
  approval_requested?: boolean;
  requested_budget?: number;
  manager_approval_status?: ManagerApprovalStatus; // Sparshak replace all of these occurences to manager_approval_status - else it looks like status of the application
  custom_strategies?: T_CustomStrategy[];
  custom_plan_idx?: number;
  intel_status?: {
    forecasting: {
      success: boolean;
      message?: string;
      last_update?: string;
      logs?: any[];
    };
    creditor_intel: {
      success: boolean;
      message?: string;
      last_update?: string;
      logs?: any[];
    };
    debtor_summary: {
      success: boolean;
      message?: string;
      last_update?: string;
      logs?: any[];
    };
    ai_audit: {
      success: boolean;
      message?: string;
      last_update?: string;
      logs?: any[];
    };
    ai_intel: {
      success: boolean;
      message?: string;
      last_update?: string;
      logs?: any[];
    };
    fia: {
      success: boolean;
      message?: string;
      last_update?: string;
      logs?: any[];
    };
  };
  ai_intel_summary?: string;
  calculator_save?: CreditorSplitFormat;
  drop_summary?: DropSummary[];
  drop_info?: { key: string; email?: string; affiliate?: string };
  notes?: string;
  protected_recievables: string[];
  negotiation_meta?: {
    hardship_flags?: string[];
  };
  payment_info?: {
    type: PaymentMethods;
    token?: string;
    email: string;
    phone: string;
    first_name: string;
    last_name: string;
    priority?: number;
    gateway: Gateway;
    store: string;
    bank_type?: "savings" | "checking";
    mask?: string;
    is_active?: boolean;
    account_name?: string;
    bank_name?: string;
    account_number?: string;
    routing_number?: string;
    paynote_user_id?: string;
    paynote_source_id?: string;
    paynote_bank_verified?: boolean;
    address?: string;
    state?: string;
    city?: string;
    zip?: string;
    cc_vault_id?: string;
    _id?: string;
  }[];
  autopay_status?: AutopayStatus;
  stages: T_MongoId[];
};

type DropSummary = {
  key: string;
  type: string;
  heading: string;
  content: any;
};

export type CreditorSplitFormat = {
  [key: string]: { start_week: number; end_week: number; amount: number }[];
};

export enum AI_INTEL {
  AUDIT = "audit",
  NEGOTIATION = "negotiation",
  BANKING = "banking",
}

export enum AI_ROLES {
  AI = "ai",
  USER = "user",
  SYSTEM = "system",
}

export enum AI_MSG_TYPE {
  MESSAGE = "message",
  REPORT = "report",
  ALERT = "alert",
}

export type T_Form = {
  check1?: boolean;
  revenue_cashflow?: string;
  check2?: boolean;
  mca_contracts?: string;
  check3?: boolean;
  liquidity_analysis?: string;
  check4?: boolean;
  risk_indicators?: string;
  check5?: boolean;
  loan_repayment?: string;
  check6?: boolean;
  credit_memo?: string;
  check7?: boolean;
  recommendations?: string;
  check8?: boolean;
  debt_analysis?: string;
  check9?: boolean;
  final_conclusion?: string;
};

export type ApplicationSchemaDto = ApplicationDto & T_MongoDoc;

export type IntelligenceDto = {
  application_id: string;
  app_key: string;
  matched_mcas?: string[];
  daily_true_profit: number;
  daily_profit_std?: number;
  daily_profit_variance?: number;
  daily_revenue_std?: number;
  daily_revenue_variance?: number;
  weekly_daily_volatility?: {
    week_start: Date;
    daily_profit_std: number;
    daily_profit_variance: number;
    daily_revenue_std: number;
    daily_revenue_variance: number;
  }[];
  monthly_daily_volatility?: {
    year: number;
    month: number;
    daily_profit_std: number | null;
    daily_profit_variance: number | null;
    daily_revenue_std: number | null;
    daily_revenue_variance: number | null;
  }[];
  monthly_true_profit_average?: {
    year: number;
    month: number;
    value: number;
    percentage: number;
    outcome: "profit" | "loss";
  }[];
  net_monthly_profit?: {
    year: number;
    month: number;
    net_value: number;
    percentage: number;
    outcome: "profit" | "loss";
  }[];
  weekly_true_profit_average_distribution?: {
    startDate: Date;
    endDate: Date;
    value: number;
    percentage: number;
    outcome: "profit" | "loss";
  }[];
  net_weekly_profit?: {
    startDate: Date;
    endDate: Date;
    net_value: number;
    percentage: number;
    outcome: "profit" | "loss";
  }[];
  net_weekly_profit_std?: number;
  net_weekly_profit_variance?: number;
  net_weekly_revenue_std?: number;
  net_weekly_revenue_variance?: number;
  net_yearly_profit_std?: number;
  net_yearly_profit_variance?: number;
  net_yearly_revenue_std?: number;
  net_yearly_revenue_variance?: number;
  weekly_credit_distribution?: {
    day: number;
    average: number;
    percentage: number;
    day_name: string;
  }[];
  ucc_scores?: {
    year: number;
    month: number;
    normalized_top_three: number;
    normalized_max_credit: number;
    normalized_top_five: number;
    month_name: string;
  }[];
  overall_ucc_score?: {
    net: {
      normalized_top_three: number;
      normalized_top_five: number;
      normalized_max_credit: number;
    };
    last_three: {
      normalized_top_three: number;
      normalized_top_five: number;
      normalized_max_credit: number;
    };
    last_six: {
      normalized_top_three: number;
      normalized_top_five: number;
      normalized_max_credit: number;
    };
  };
  // mca_wise?: {
  //     name: string;
  //     transactions: {
  //         account_number: string;
  //         credit_amount: number;
  //         debit_amount: number;
  //         transaction_date: string;
  //         description: string;
  //     }[];
  //     average: number;
  // };
  net_monthly_profit_std: number;
  net_monthly_profit_variance: number;
  net_monthly_revenue_std: number;
  net_monthly_revenue_variance: number;
  mca_wise: any;
  monthly_profit_summary: {
    year: number;
    month: number;
    total_revenue: number;
    total_expenses: number;
    net_profit: number;
    avg_daily_true_profit: number;
    std_daily_profit: number;
    var_daily_profit: number;
    std_daily_revenue: number;
    var_daily_revenue: number;
  }[];
  net_margin_summary: {
    total_revenue: number;
    total_expenses: number;
    net_profit: number;
    net_margin_percent: number;
  };
  net_yearly_profit: [
    {
      year: number;
      net_value: number;
      revenue: number;
    },
  ];
  cash_flow_stability_index?: number;
  coefficient_of_variation: number;
  cash_flow_volatility_risk: string;
  relative_volatility_risk: string;
  daily_balance_std_dev?: number;
  daily_ending_balances?: { avg_ending_balance: number; std_dev: number; min: number; max: number };
  deposit_summary?: {
    number_of_deposits: number;
    total_deposit_amount: number;
    average_deposit: number;
    std_dev: number;
    min: number;
    max: number;
    percentiles: {
      "25th": number;
      "50th (median)": number;
      "75th": number;
    };
  };
  negative_balance_days?: number;
  payroll_to_revenue: {
    total_payroll: number;
    total_revenue: number;
    payroll_to_revenue_percent: number;
  };
  expense_to_revenue?: {
    total_expenses: number;
    total_revenue: number;
    expense_to_revenue_percent: number;
  };
  balance_volatility_risk: string;
  average_daily_closing_balance?: number;
  min_daily_balance?: number;
  cash_reserve_ratio?: number;
  liquidity_risk?: string;
  liquidity_risk_index?: number;
  savings_rate_percentage?: number;
  average_payment_days?: number;
  revenue_distribution_stats?: {
    year: number;
    month: number;
    min: number;
    max: number;
    mean: number;
    median: number;
    mode: number;
  }[];
  flagged_credit_payments?: {
    account_number: string;
    date: Date;
    credit_amount: number;
    description: string;
  }[];
  category_recurrence_analysis?: {
    category: string;
    recurrence_pattern: string;
    best_recurrence_fit: string;
    total_transactions: number;
    average_amount: number;
    min_amount: number;
    min_date: string;
    max_amount: number;
    max_amount_receiving_date: string;
    mean: number;
    median: number;
    mode: number;
  }[];
  probability_of_default?: {
    delinquency_history: {
      thirty_days_past_due: number;
      sixty_days_past_due: number;
      ninety_days_past_due: number;
    };
    prior_defaults: number;
    charge_offs: number;
    mca_lenders: string[];
    total_mca_transactions: number;
    mca_payments_total: number;
    pd_score: number;
  };
  debt_to_income_ratio: {
    monthly_dti: {
      year: number;
      month: number;
      income: number;
      debt_payments: number;
      dti_percentage: number;
    }[];
    quarterly_dti: {
      year: number;
      quarter: number;
      income: number;
      debt_payments: number;
      dti_percentage: number;
    }[];
    total_outstanding_debt: number;
    monthly_debt_obligations: number;
    average_monthly_dti: number;
    debt_breakdown: {
      "MCA Payments": number;
      Mortgage: number;
      "Auto Loans": number;
      "Credit Cards": number;
    };
  };
  behavioral_analysis: {
    withdrawal_analysis: {
      total_count: number;
      total_value: number;
      average_withdrawal: number;
      monthly_withdrawals: {
        year: number;
        month: number;
        count: number;
        value: number;
      }[];
    };
    spending_breakdown: {
      categories: {
        category: string;
        amount: number;
        count: number;
      }[];
      essential_to_discretionary_ratio: number;
    };
    high_risk_transactions: {
      total_count: number;
      total_value: number;
      transactions: any;
    };
    liquidity_ratio: {
      liquid_assets: number;
      monthly_obligations: number;
      ratio: number;
      risk_level: string;
    };
    savings_behavior: {
      total_transfers: number;
      total_value: number;
      monthly_savings: any;
    };
    nsf_metrics: {
      total_count: number;
      total_fees: number;
      nsf_events: [
        {
          date: Date;
          amount: number;
          description: string;
        },
      ];
    };
    transaction_fabrication: {
      potential_loops: [
        {
          outgoing_date: Date;
          incoming_date: Date;
          time_diff_hours: number;
          outgoing_amount: number;
          incoming_amount: number;
          outgoing_description: string;
          incoming_description: string;
        },
      ];
      total_loop_count: number;
      risk_level: string;
    };
    debt_coverage: {
      monthly_breakdown: {
        year: number;
        month: number;
        net_income: number;
        debt_obligations: number;
        debt_coverage_ratio: number;
      }[];
      average_monthly_income: number;
      average_monthly_debt: number;
      average_debt_coverage_ratio: number;
      risk_level: string;
    };
    // late_payments_overleveraging: {

    // }
  };
};
